import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Plus, RefreshCw, Trash2, FileText, Download, CheckCircle2,
  Package, ExternalLink, Pencil, X, Percent, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_FORM_SECTIONS = [
  { id: "A", title: "A — Particulars",
    fields: [
      { key: "A.effective_date", label: "Effective date", type: "date" },
      { key: "A.term_months", label: "Initial term (months)", type: "number" },
      { key: "A.auto_renewal_months", label: "Auto-renewal (months)", type: "number" },
      { key: "A.non_renewal_notice_days", label: "Non-renewal notice (days)", type: "number" },
    ]},
  { id: "B", title: "B — Merchant",
    fields: [
      { key: "B.legal_name", label: "Legal name" },
      { key: "B.trading_name", label: "Trading name" },
      { key: "B.registration_number", label: "Registration number" },
      { key: "B.registration_country", label: "Registration country" },
      { key: "B.registered_address", label: "Registered address" },
      { key: "B.tax_id", label: "Tax ID" },
      { key: "B.website", label: "Website" },
      { key: "B.primary_contact_name", label: "Primary contact name" },
      { key: "B.primary_contact_email", label: "Primary contact email" },
      { key: "B.primary_contact_phone", label: "Primary contact phone" },
      { key: "B.primary_contact_role", label: "Primary contact role" },
    ]},
  { id: "D", title: "D — Acquirer",
    fields: [
      { key: "D.solution_public_name", label: "Solution name" },
      { key: "D.acquirer_provider", label: "Provider" },
      { key: "D.acquiring_bank", label: "Acquiring bank" },
      { key: "D.acquiring_bank_country", label: "Bank country" },
      { key: "D.settlement_currency", label: "Settlement ccy" },
      { key: "D.mid_currency", label: "MID ccy" },
      { key: "D.settlement_terms_days", label: "Settlement days", type: "number" },
    ]},
  { id: "E", title: "E — Vertical",
    fields: [
      { key: "E.industry", label: "Industry" },
      { key: "E.mcc", label: "MCC code" },
      { key: "E.high_risk_vertical", label: "High-risk vertical", type: "checkbox" },
      { key: "E.chargeback_threshold_pct", label: "Chargeback threshold (%)", type: "number" },
    ]},
  { id: "F", title: "F — Pricing",
    fields: [
      { key: "F.discount_rate_pct", label: "Discount rate (%)", type: "number" },
      { key: "F.transaction_fee_amount", label: "Per-txn fee", type: "number" },
      { key: "F.monthly_fee_amount", label: "Monthly fee", type: "number" },
      { key: "F.setup_fee_amount", label: "Setup fee", type: "number" },
      { key: "F.chargeback_fee_amount", label: "Chargeback fee", type: "number" },
      { key: "F.refund_fee_amount", label: "Refund fee", type: "number" },
      { key: "F.rolling_reserve_pct", label: "Rolling reserve (%)", type: "number" },
      { key: "F.rolling_reserve_days", label: "Reserve days", type: "number" },
    ]},
  { id: "G", title: "G — Volume Forecast & Velocity",
    fields: [
      { key: "G.forecast_monthly_volume", label: "Forecast monthly volume", type: "number" },
      { key: "G.forecast_monthly_transactions", label: "Forecast monthly txns", type: "number" },
      { key: "G.forecast_average_ticket", label: "Forecast avg ticket", type: "number" },
      { key: "G.forecast_max_ticket", label: "Forecast max ticket", type: "number" },
      { key: "G.velocity_monthly_volume_cap", label: "Velocity — monthly vol cap", type: "number" },
      { key: "G.velocity_monthly_txn_cap", label: "Velocity — monthly txn cap", type: "number" },
      { key: "G.velocity_daily_volume_cap", label: "Velocity — daily vol cap", type: "number" },
      { key: "G.velocity_max_ticket", label: "Velocity — max ticket", type: "number" },
    ]},
  { id: "K", title: "K — Special Provisions",
    fields: [
      { key: "K.special_provisions", label: "Special provisions", type: "textarea" },
    ]},
];

function OrderFormEditor({ merchantId, assignmentId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ A: true, B: true, D: true, F: true, G: true, K: true, E: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/admin/merchants/${merchantId}/solutions/${assignmentId}/order-form`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      const body = await r.json();
      setData(body);
      setDraft({});
    } catch (err) {
      toast.error("Failed to load order form");
    } finally {
      setLoading(false);
    }
  }, [merchantId, assignmentId]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    if (Object.keys(draft).length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      const overrides = {};
      Object.entries(draft).forEach(([k, v]) => {
        const fld = ORDER_FORM_SECTIONS.flatMap((s) => s.fields).find((f) => f.key === k);
        if (fld?.type === "number") {
          overrides[k] = v === "" ? null : Number(v);
        } else if (fld?.type === "checkbox") {
          overrides[k] = !!v;
        } else {
          overrides[k] = v;
        }
      });
      const r = await fetch(
        `${API}/admin/merchants/${merchantId}/solutions/${assignmentId}/order-form`,
        {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ overrides }),
        },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      const result = await r.json();
      toast.success(`Saved ${result.fields_updated} field(s)`);
      load();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const issue = async () => {
    if (!window.confirm("Issue this order form? It becomes the legal record and future edits require a new draft.")) return;
    try {
      const r = await fetch(
        `${API}/admin/merchants/${merchantId}/solutions/${assignmentId}/order-form/issue`,
        { method: "POST", credentials: "include" },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      toast.success("Order form issued");
      load();
    } catch (err) {
      toast.error("Issue failed");
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await fetch(
        `${API}/admin/merchants/${merchantId}/solutions/${assignmentId}/order-form/pdf`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NP-FORM-002_${merchantId}_${assignmentId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("PDF download failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-500" data-testid="order-form-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading order form…
      </div>
    );
  }
  if (!data) return null;

  const merged = { ...data.merged, ...draft };
  const status = data.order_form?.status || "draft";

  return (
    <div className="fixed inset-0 z-50 flex items-stretch bg-black/75 backdrop-blur-sm" data-testid="order-form-editor">
      <div className="ml-auto h-full w-full max-w-4xl bg-[#0a0a0a] border-l border-white/[0.08] overflow-y-auto">
        <header className="sticky top-0 bg-[#0a0a0a] border-b border-white/[0.08] px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">— Order Form NP-FORM-002</div>
              <h2 className="text-[18px] font-light text-white mt-1">Edit & Issue</h2>
              <p className="text-[11.5px] text-zinc-500 mt-1">
                Status: <span className={status === "draft" ? "text-amber-300" : "text-emerald-300"}>{status.toUpperCase()}</span>
                {" · "}{Object.keys(draft).length} pending field edit(s)
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={downloadPdf} data-testid="order-form-pdf-btn"
                className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-zinc-300 hover:text-white inline-flex items-center gap-1.5">
                <Download className="h-3.5 w-3.5" /> Download PDF
              </button>
              <button onClick={save} disabled={saving || Object.keys(draft).length === 0}
                data-testid="order-form-save-btn"
                className="rounded-xl px-3 py-2 text-[12.5px] bg-white text-black hover:bg-zinc-200 disabled:opacity-40 inline-flex items-center gap-1.5">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />}
                Save edits
              </button>
              {status === "draft" && (
                <button onClick={issue} data-testid="order-form-issue-btn"
                  className="rounded-xl px-3 py-2 text-[12.5px] bg-emerald-400 text-black hover:bg-emerald-300 inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Issue
                </button>
              )}
              <button onClick={onClose} className="rounded-xl p-2 text-zinc-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </header>

        <div className="px-6 py-6 space-y-5">
          {ORDER_FORM_SECTIONS.map((section) => (
            <section key={section.id} className="rounded-2xl border border-white/[0.06] bg-white/[0.01]" data-testid={`order-form-section-${section.id}`}>
              <button
                type="button"
                onClick={() => setExpanded((e) => ({ ...e, [section.id]: !e[section.id] }))}
                className="w-full flex items-center justify-between px-5 py-3"
                data-testid={`order-form-section-toggle-${section.id}`}
              >
                <span className="text-[13px] font-medium text-white">{section.title}</span>
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
                  {expanded[section.id] ? "Hide" : "Show"}
                </span>
              </button>
              {expanded[section.id] && (
                <div className="px-5 pb-5 grid grid-cols-2 gap-3 border-t border-white/[0.04] pt-4">
                  {section.fields.map((f) => {
                    const value = merged[f.key];
                    const isDirty = draft.hasOwnProperty(f.key);
                    if (f.type === "checkbox") {
                      return (
                        <label key={f.key} className="flex items-center gap-2 col-span-2 text-[12.5px] text-zinc-300">
                          <input
                            type="checkbox"
                            checked={!!value}
                            onChange={(e) => setField(f.key, e.target.checked)}
                            data-testid={`order-form-input-${f.key}`}
                            className="rounded border-white/[0.10] bg-white/[0.05]"
                          />
                          {f.label}
                          {isDirty && <span className="text-amber-300 text-[10px] ml-2">edited</span>}
                        </label>
                      );
                    }
                    if (f.type === "textarea") {
                      return (
                        <div key={f.key} className="col-span-2">
                          <label className="block text-[10.5px] uppercase tracking-[0.16em] text-zinc-500 mb-1">
                            {f.label}
                            {isDirty && <span className="text-amber-300 text-[10px] ml-2">edited</span>}
                          </label>
                          <textarea
                            rows={3}
                            value={value ?? ""}
                            onChange={(e) => setField(f.key, e.target.value)}
                            data-testid={`order-form-input-${f.key}`}
                            className="w-full rounded-lg border border-white/[0.10] bg-white/[0.03] px-2.5 py-1.5 text-[12.5px] text-white focus:outline-none focus:border-[#00E5FF]/40"
                          />
                        </div>
                      );
                    }
                    return (
                      <div key={f.key}>
                        <label className="block text-[10.5px] uppercase tracking-[0.16em] text-zinc-500 mb-1">
                          {f.label}
                          {isDirty && <span className="text-amber-300 text-[10px] ml-2">edited</span>}
                        </label>
                        <input
                          type={f.type || "text"}
                          step={f.type === "number" ? "0.01" : undefined}
                          value={value ?? ""}
                          onChange={(e) => setField(f.key, e.target.value)}
                          data-testid={`order-form-input-${f.key}`}
                          className={`w-full rounded-lg border bg-white/[0.03] px-2.5 py-1.5 text-[12.5px] text-white focus:outline-none focus:border-[#00E5FF]/40 ${
                            isDirty ? "border-amber-400/40" : "border-white/[0.10]"
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function AssignSolutionModal({ merchantId, onClose, onAssigned }) {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadSolutions = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/solutions?status=active`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setSolutions(data.solutions || []);
    } catch (_) {
      toast.error("Failed to load solutions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSolutions(); }, [loadSolutions]);

  // Empty catalog fallback — production deploys sometimes ship without
  // `scripts/solutions.json`, so the auto-import on boot is a no-op
  // and the dropdown looks broken. One-click "Import default catalog"
  // gives AMs an unblock path without a redeploy.
  const importDefaultCatalog = async () => {
    setImporting(true);
    try {
      const r = await fetch(`${API}/admin/solutions/import-from-brief`, {
        method: "POST", credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `${r.status}`);
      toast.success(`Imported ${data.inserted ?? 0} new + updated ${data.updated ?? 0} existing solutions`);
      await loadSolutions();
    } catch (err) {
      const msg = String(err.message || "");
      if (msg === "403" || msg.toLowerCase().includes("super_admin")
          || msg.toLowerCase().includes("forbidden")) {
        toast.error("Import failed: requires super_admin. Ask a super admin to run the import.");
      } else {
        toast.error(`Import failed: ${msg}. Check that backend/scripts/solutions.json is deployed.`);
      }
    } finally {
      setImporting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const r = await fetch(
        `${API}/admin/merchants/${merchantId}/solutions`,
        {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            solution_id: selectedId,
            notes: notes.trim() || null,
          }),
        },
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `${r.status}`);
      toast.success("Solution assigned with snapshot");
      onAssigned(data);
      onClose();
    } catch (err) {
      toast.error(String(err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="assign-solution-modal">
      <form onSubmit={submit} className="w-full max-w-lg mx-4 rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-6">
        <h2 className="text-[14px] font-medium text-white mb-1">Assign solution</h2>
        <p className="text-[12px] text-zinc-500 mb-5">
          Rates are snapshotted at assignment time. Future master changes won&apos;t auto-propagate (use Refresh).
        </p>
        <label className="block text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Solution</label>
        {!loading && solutions.length === 0 && (
          <div
            data-testid="assign-solution-empty-state"
            className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] p-4 mb-4"
          >
            <div className="text-[12.5px] text-amber-200 mb-1 font-medium">
              No active solutions in catalog
            </div>
            <p className="text-[11.5px] text-amber-100/80 mb-3 leading-relaxed">
              The solutions catalog is empty on this environment. This usually
              means the boot-time auto-import didn&apos;t find{" "}
              <code className="text-amber-100">backend/scripts/solutions.json</code>.
              Click below to seed the 25-record default catalog now (idempotent — safe to re-run).
            </p>
            <button
              type="button"
              onClick={importDefaultCatalog}
              disabled={importing}
              data-testid="assign-solution-import-default-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-300 hover:bg-amber-200 text-black text-[12px] px-3 py-1.5 disabled:opacity-50"
            >
              {importing && <Loader2 className="h-3 w-3 animate-spin" />}
              Import default catalog
            </button>
          </div>
        )}
        <select required value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
          data-testid="assign-solution-select"
          disabled={loading}
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white mb-4">
          <option value="">{loading ? "Loading…" : "— Pick a solution —"}</option>
          {solutions.map((s) => (
            <option key={s.solution_id} value={s.solution_id}>
              {s.name} ({s.acquiring_bank || s.settlement_currency})
            </option>
          ))}
        </select>
        <label className="block text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Notes (optional)</label>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          data-testid="assign-solution-notes"
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white mb-5" />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-[12.5px] text-zinc-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={!selectedId || submitting}
            data-testid="assign-solution-submit"
            className="rounded-xl px-4 py-2 text-[12.5px] bg-white text-black hover:bg-zinc-200 disabled:opacity-40 inline-flex items-center gap-1.5">
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />} Assign
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Merchant Solutions section — assignment list + Order Form editor entry.
 * Drop-in: render inside `AdminMerchantDetail` as a new tab/section.
 */
export default function MerchantSolutions({ merchantId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editorAid, setEditorAid] = useState(null);
  const [negotiatingAid, setNegotiatingAid] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/merchants/${merchantId}/solutions`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setRows(data.assignments || []);
    } catch (_) {
      toast.error("Failed to load assigned solutions");
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async (aid) => {
    try {
      const r = await fetch(
        `${API}/admin/merchants/${merchantId}/solutions/${aid}/refresh-from-master`,
        { method: "POST", credentials: "include" },
      );
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data?.detail || `${r.status}`);
      }
      toast.success("Snapshot refreshed");
      load();
    } catch (err) {
      toast.error(String(err.message));
    }
  };

  const remove = async (aid, name) => {
    if (!window.confirm(`Remove assignment to ${name}? This cannot be undone.`)) return;
    try {
      const r = await fetch(
        `${API}/admin/merchants/${merchantId}/solutions/${aid}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data?.detail || `${r.status}`);
      }
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(String(err.message));
    }
  };

  return (
    <section data-testid="merchant-solutions-section" className="mt-8">
      <header className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">— Solutions</div>
          <h2 className="text-[14px] font-medium text-white">Assigned solutions</h2>
          <p className="text-[11.5px] text-zinc-500 mt-1">
            Per-merchant solution catalog. Rates are snapshotted at assignment. Generate the Order Form (NP-FORM-002) from any assignment.
          </p>
        </div>
        <button
          onClick={() => setAssignOpen(true)}
          data-testid="merchant-assign-solution-btn"
          className="rounded-xl px-3 py-2 text-[12.5px] bg-white text-black hover:bg-zinc-200 inline-flex items-center gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" /> Assign solution
        </button>
      </header>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-zinc-500" data-testid="merchant-solutions-loading">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-zinc-500" data-testid="merchant-solutions-empty">
            <Package className="h-6 w-6 mx-auto mb-3 text-zinc-700" />
            <div className="text-[13px] mb-1">No solutions assigned</div>
            <div className="text-[11.5px] text-zinc-600">Assign one to start generating order forms.</div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06] text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="text-left py-3 px-5 font-normal">Solution</th>
                <th className="text-left py-3 px-5 font-normal">Acquiring bank</th>
                <th className="text-left py-3 px-5 font-normal">Snapshot</th>
                <th className="text-left py-3 px-5 font-normal">Status</th>
                <th className="text-right py-3 px-5 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const snap = a.snapshot || {};
                return (
                  <tr key={a.assignment_id} data-testid={`assignment-row-${a.assignment_id}`}
                    className="border-b border-white/[0.04]">
                    <td className="py-3 px-5">
                      <div className="text-[13px] text-white">{snap.name || "—"}</div>
                      <div className="text-[10.5px] text-zinc-500 font-mono">
                        {snap.settlement_currency || "—"}
                        {snap.mid_currency && snap.mid_currency !== snap.settlement_currency &&
                          ` / ${snap.mid_currency}`}
                      </div>
                      {/* Brief 3.15 (Feb 27 2026) — negotiated fees badge.
                         Once the sales→ops handover replaces the catalog
                         sell-rate cells with the actual deal numbers,
                         show how many fields were overridden so the AM
                         doesn't have to open the modal to know. */}
                      {Object.keys(a.override_sell_rates || {}).length > 0 && (
                        <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-200 bg-amber-400/[0.08] border border-amber-400/30 rounded-full px-2 py-0.5"
                              data-testid={`assignment-negotiated-badge-${a.assignment_id}`}>
                          <Percent className="h-2.5 w-2.5" />
                          {Object.keys(a.override_sell_rates).length} negotiated
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-5 text-[12.5px] text-zinc-300">
                      {snap.acquiring_bank || "—"}
                      {snap.acquiring_bank_country && (
                        <span className="text-[10.5px] text-zinc-500 ml-1.5">· {snap.acquiring_bank_country}</span>
                      )}
                    </td>
                    <td className="py-3 px-5 text-[11.5px] text-zinc-500 font-mono">
                      {(a.snapshot_taken_at || "").slice(0, 10)}
                    </td>
                    <td className="py-3 px-5">
                      <span className={`inline-flex items-center text-[10.5px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${
                        a.status === "active"
                          ? "bg-emerald-500/[0.10] text-emerald-300 border-emerald-400/30"
                          : "bg-zinc-500/[0.10] text-zinc-400 border-zinc-500/30"
                      }`}>{a.status}</span>
                    </td>
                    <td className="py-3 px-5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setEditorAid(a.assignment_id)}
                          data-testid={`assignment-order-form-btn-${a.assignment_id}`}
                          title="Edit / issue Order Form"
                          className="rounded-lg p-1.5 text-zinc-400 hover:text-white">
                          <FileText className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => setNegotiatingAid(a.assignment_id)}
                          data-testid={`assignment-negotiate-btn-${a.assignment_id}`}
                          title="Input negotiated final fees"
                          className={`rounded-lg p-1.5 hover:text-white ${
                            Object.keys(a.override_sell_rates || {}).length > 0
                              ? "text-amber-300"
                              : "text-zinc-400"
                          }`}>
                          <Percent className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => refresh(a.assignment_id)}
                          data-testid={`assignment-refresh-btn-${a.assignment_id}`}
                          title="Refresh snapshot from master"
                          className="rounded-lg p-1.5 text-zinc-400 hover:text-white">
                          <RefreshCw className="h-3.5 w-3.5" />
                        </button>
                        <Link to={`/admin/solutions/${a.solution_id}`}
                          data-testid={`assignment-view-solution-${a.assignment_id}`}
                          title="View master solution"
                          className="rounded-lg p-1.5 text-zinc-400 hover:text-white">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        <button onClick={() => remove(a.assignment_id, snap.name)}
                          data-testid={`assignment-remove-btn-${a.assignment_id}`}
                          title="Remove assignment"
                          className="rounded-lg p-1.5 text-zinc-400 hover:text-red-400">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {assignOpen && (
        <AssignSolutionModal
          merchantId={merchantId}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => load()}
        />
      )}
      {editorAid && (
        <OrderFormEditor
          merchantId={merchantId}
          assignmentId={editorAid}
          onClose={() => { setEditorAid(null); load(); }}
        />
      )}
      {negotiatingAid && (
        <NegotiatedFeesModal
          merchantId={merchantId}
          assignment={rows.find((a) => a.assignment_id === negotiatingAid)}
          onClose={() => setNegotiatingAid(null)}
          onSaved={() => { setNegotiatingAid(null); load(); }}
        />
      )}
    </section>
  );
}

// ============================================================================
// Brief 3.15 (Feb 27 2026) — NegotiatedFeesModal.
//
// Workflow: sales pitches with the catalog `sell_rates`. Once the deal
// closes and ops assigns the solution to the live merchant, the AM
// opens this modal to record the FINAL negotiated fees — typically a
// few cells different from catalog (e.g. discount_rate_pct shaved
// 30bps for volume commitment). Only the cells the AM actually edits
// are persisted as `override_sell_rates` — unchanged cells fall back
// to the catalog snapshot at quote time.
//
// Margin gate (visual only — no hard block): if buy_rates is present
// on the snapshot, each row computes margin = buy - negotiated and
// flags amber when below MIN_DISCOUNT_RATE_PP (0.5pp on %, 0.10 on
// per-txn). Rose if outright negative. AM can still save through it
// — audit log captures the deal context.
// ============================================================================

const RATE_FIELDS = [
  { key: "discount_rate_pct", label: "Discount rate", unit: "%", kind: "pct" },
  { key: "settlement_fee_pct", label: "Settlement fee", unit: "%", kind: "pct" },
  { key: "transaction_fee_amount", label: "Per-transaction fee", unit: "abs", kind: "amt" },
  { key: "monthly_fee_amount", label: "Monthly fee", unit: "abs", kind: "amt" },
  { key: "setup_fee_amount", label: "Setup fee", unit: "abs", kind: "amt" },
  { key: "chargeback_fee_amount", label: "Chargeback fee", unit: "abs", kind: "amt" },
  { key: "rolling_reserve_pct", label: "Rolling reserve", unit: "%", kind: "pct" },
  { key: "rolling_reserve_days", label: "Rolling reserve hold", unit: "days", kind: "days" },
];

const MIN_PCT_MARGIN = 0.5;
const MIN_AMT_MARGIN = 0.10;

function NegotiatedFeesModal({ merchantId, assignment, onClose, onSaved }) {
  const snap = assignment?.snapshot || {};
  const catalogSell = snap.sell_rates || {};
  const catalogBuy = snap.buy_rates || {};
  const existingOverride = assignment?.override_sell_rates || {};

  // Pre-fill values: existing override → catalog → empty
  const initial = RATE_FIELDS.reduce((acc, f) => {
    const v = existingOverride[f.key];
    acc[f.key] = (v != null ? v : (catalogSell[f.key] ?? ""));
    return acc;
  }, {});

  const [values, setValues] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const setField = (key, raw) => {
    setValues((v) => ({ ...v, [key]: raw === "" ? "" : raw }));
  };

  // Compute the diff that will actually be submitted as the override.
  // Only cells that differ from catalog get persisted.
  const buildOverride = () => {
    const out = {};
    for (const f of RATE_FIELDS) {
      const raw = values[f.key];
      if (raw === "" || raw == null) continue;  // skip blank
      const num = Number(raw);
      if (Number.isNaN(num)) continue;
      const catalog = catalogSell[f.key];
      if (catalog == null || Number(catalog) !== num) {
        out[f.key] = num;
      }
    }
    return out;
  };

  const overrideDiff = buildOverride();
  const negotiatedCount = Object.keys(overrideDiff).length;

  const marginFor = (field) => {
    const raw = values[field.key];
    if (raw === "" || raw == null) return null;
    const sell = Number(raw);
    const buy = Number(catalogBuy[field.key]);
    if (Number.isNaN(sell) || Number.isNaN(buy)) return null;
    return Number((sell - buy).toFixed(4));
  };

  const marginAlertFor = (field) => {
    const m = marginFor(field);
    if (m == null) return null;
    if (m < 0) return "negative";
    const gate = field.kind === "pct" ? MIN_PCT_MARGIN : MIN_AMT_MARGIN;
    if (field.kind === "days") return null;
    if (m < gate) return "thin";
    return null;
  };

  const save = async () => {
    setSubmitting(true);
    const toastId = toast.loading("Saving negotiated fees…");
    try {
      const r = await fetch(
        `${API}/admin/merchants/${merchantId}/solutions/${assignment.assignment_id}`,
        { method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ override_sell_rates: overrideDiff }) });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data?.detail || `${r.status}`);
      }
      toast.success(
        negotiatedCount === 0
          ? "Cleared all overrides — using catalog rates"
          : `Saved ${negotiatedCount} negotiated fee${negotiatedCount === 1 ? "" : "s"}`,
        { id: toastId, duration: 4000 });
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${err.message}`, { id: toastId, duration: 8000 });
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-3xl rounded-2xl border border-white/[0.08] bg-[#0a0d12] p-6 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           role="dialog" data-testid="negotiated-fees-modal">
        <header className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Negotiated fees</div>
            <div className="text-white text-[15px] mt-1">{snap.name || "Solution"}</div>
            <div className="text-zinc-500 text-[12px] mt-1 leading-relaxed">
              Input the final fees the merchant agreed to pay. Catalog values
              are pre-filled — change only the cells that were negotiated.
              Unchanged cells fall back to the catalog at quote/invoice time.
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting}
            data-testid="negotiated-fees-close"
            className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="rounded-xl border border-white/[0.06] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06] text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 bg-white/[0.015]">
                <th className="text-left py-2.5 px-4 font-normal">Field</th>
                <th className="text-right py-2.5 px-4 font-normal">Catalog</th>
                <th className="text-right py-2.5 px-4 font-normal">Negotiated</th>
                <th className="text-right py-2.5 px-4 font-normal">Margin</th>
              </tr>
            </thead>
            <tbody>
              {RATE_FIELDS.map((f) => {
                const catalog = catalogSell[f.key];
                const margin = marginFor(f);
                const alert = marginAlertFor(f);
                const isOverridden = f.key in overrideDiff;
                const buy = catalogBuy[f.key];
                return (
                  <tr key={f.key} className="border-b border-white/[0.04]"
                      data-testid={`negotiated-fees-row-${f.key}`}>
                    <td className="py-2.5 px-4 text-[12.5px] text-zinc-300">
                      {f.label}
                      {isOverridden && (
                        <span className="ml-2 inline-flex items-center text-[10px] text-amber-200">
                          <Pencil className="h-2.5 w-2.5 mr-0.5" />
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-[12.5px] text-zinc-500 text-right font-mono">
                      {catalog != null ? `${catalog}${f.unit === "%" ? "%" : f.unit === "days" ? " d" : ""}` : "—"}
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <input
                          type="number"
                          step={f.kind === "days" ? "1" : "0.01"}
                          value={values[f.key] ?? ""}
                          onChange={(e) => setField(f.key, e.target.value)}
                          data-testid={`negotiated-input-${f.key}`}
                          className={`w-24 rounded-md border bg-white/[0.03] px-2 py-1 text-[12.5px] text-white text-right focus:outline-none focus:border-[#00E5FF]/40 ${
                            isOverridden ? "border-amber-400/40" : "border-white/[0.10]"
                          }`}
                        />
                        <span className="text-[11px] text-zinc-500 w-3 text-left">
                          {f.unit === "%" ? "%" : f.unit === "days" ? "d" : ""}
                        </span>
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-right text-[12px] font-mono">
                      {margin == null ? (
                        <span className="text-zinc-600">buy n/a</span>
                      ) : alert === "negative" ? (
                        <span className="text-rose-300 inline-flex items-center gap-1"
                              data-testid={`negotiated-margin-alert-${f.key}`}>
                          <AlertTriangle className="h-3 w-3" />
                          {margin}{f.unit === "%" ? "pp" : ""}
                        </span>
                      ) : alert === "thin" ? (
                        <span className="text-amber-300 inline-flex items-center gap-1"
                              data-testid={`negotiated-margin-alert-${f.key}`}>
                          <AlertTriangle className="h-3 w-3" />
                          {margin}{f.unit === "%" ? "pp" : ""}
                        </span>
                      ) : (
                        <span className="text-emerald-300">
                          {margin}{f.unit === "%" ? "pp" : ""}
                        </span>
                      )}
                      {buy != null && (
                        <div className="text-[10px] text-zinc-600">buy: {buy}{f.unit === "%" ? "%" : ""}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-[11.5px] text-zinc-500 leading-relaxed">
          {negotiatedCount > 0 ? (
            <span className="text-amber-200">{negotiatedCount} field{negotiatedCount === 1 ? "" : "s"} will be saved as negotiated overrides.</span>
          ) : (
            <span>No cells differ from catalog — saving will clear any existing overrides on this assignment.</span>
          )}
          {" "}Margins coloured amber/rose are below thresholds (0.5pp / 0.10 abs) — they don&apos;t block save, but the deal will fire a low-margin alert in the BO.
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid="negotiated-fees-cancel"
                  className="rounded-xl px-4 py-2 text-[12.5px] text-zinc-400 hover:text-white">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={submitting}
                  data-testid="negotiated-fees-save"
                  className="rounded-xl px-4 py-2 text-[12.5px] bg-white text-black hover:bg-zinc-200 disabled:opacity-40 inline-flex items-center gap-1.5">
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
            Save negotiated fees
          </button>
        </div>
      </div>
    </div>
  );
}
